import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Divider,
} from "@mui/material";
import MultiSelectDropdown from "../common/MultiSelectDropdown";
import {
    getAvailableRoomsForFolder,
    linkRoomsToFolder,
    unlinkRoomsFromFolder,
    StenomaticRoom,
} from "../../api/stenomatic";
import { useNotification } from "../../context/NotificationContext";

interface RoomLinkingModalProps {
    open: boolean;
    onClose: () => void;
    folderId: string;
    folderName: string;
    onSuccess?: () => void;
}

const RoomLinkingModal: React.FC<RoomLinkingModalProps> = ({ open, onClose, folderId, folderName, onSuccess }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    const [availableRooms, setAvailableRooms] = useState<StenomaticRoom[]>([]);
    const [linkedRooms, setLinkedRooms] = useState<StenomaticRoom[]>([]);
    const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch available and linked rooms
    useEffect(() => {
        if (open && folderId) {
            fetchRooms();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, folderId]);

    const fetchRooms = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                setError(t("noTokenFound"));
                return;
            }

            const response = await getAvailableRoomsForFolder(token, folderId);
            setAvailableRooms(response.availableRooms);
            setLinkedRooms(response.linkedRooms);
        } catch (error) {
            console.error("Error fetching rooms:", error);
            setError(t("errorFetchingRooms"));
        } finally {
            setLoading(false);
        }
    };

    const handleRoomSelection = (selectedValues: string[]) => {
        setSelectedRoomIds(selectedValues);
    };

    const handleLinkRooms = async () => {
        if (selectedRoomIds.length === 0) return;

        setSubmitting(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                showNotification({
                    message: t("noTokenFound"),
                    type: "error",
                });
                return;
            }

            await linkRoomsToFolder(token, folderId, selectedRoomIds);

            showNotification({
                message: t("roomsLinkedSuccessfully"),
                type: "success",
            });

            // Refresh the rooms list
            await fetchRooms();
            setSelectedRoomIds([]);

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("Error linking rooms:", error);
            showNotification({
                message: t("errorLinkingRooms"),
                type: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleUnlinkRoom = async (roomId: string) => {
        setSubmitting(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                showNotification({
                    message: t("noTokenFound"),
                    type: "error",
                });
                return;
            }

            await unlinkRoomsFromFolder(token, folderId, [roomId]);

            showNotification({
                message: t("roomUnlinkedSuccessfully"),
                type: "success",
            });

            // Refresh the rooms list
            await fetchRooms();

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("Error unlinking room:", error);
            showNotification({
                message: t("errorUnlinkingRoom"),
                type: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedRoomIds([]);
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                root: { sx: { zIndex: 10000 } },
            }}
            PaperProps={{
                sx: { borderRadius: 3, minHeight: "500px" },
            }}
        >
            <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
                {t("manageRoomLinks")}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: "normal" }}>
                    {t("manageRoomLinksFor")} <strong>{folderName}</strong>
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {t("loadingRooms")}
                        </Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <Box>
                        {/* Currently Linked Rooms */}
                        <Box mb={3}>
                            <Typography variant="h6" gutterBottom>
                                {t("currentlyLinkedRooms")} ({linkedRooms.length})
                            </Typography>

                            {linkedRooms.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    {t("noLinkedRooms")}
                                </Typography>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {linkedRooms.map((room) => (
                                        <Chip
                                            key={room.id}
                                            label={room.title}
                                            onDelete={() => handleUnlinkRoom(room.id)}
                                            disabled={submitting}
                                            variant="outlined"
                                            sx={{
                                                borderColor: "#3ACADA",
                                                color: "#3ACADA",
                                                "&:hover": {
                                                    backgroundColor: "rgba(58, 202, 218, 0.08)",
                                                },
                                                "& .MuiChip-deleteIcon": {
                                                    color: "#3ACADA",
                                                    "&:hover": {
                                                        color: "#2bb5c9",
                                                    },
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Add New Room Links */}
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {t("addRoomLinks")}
                            </Typography>

                            {availableRooms.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    {t("noAvailableRooms")}
                                </Typography>
                            ) : (
                                <Box sx={{ mb: 2 }}>
                                    <MultiSelectDropdown
                                        label=""
                                        placeholder={t("selectRoomsToLink")}
                                        options={availableRooms.map((room) => ({
                                            value: room.id,
                                            label: room.title + (room.description ? ` - ${room.description}` : ""),
                                        }))}
                                        value={selectedRoomIds}
                                        onChange={handleRoomSelection}
                                        disabled={submitting}
                                        loading={false}
                                        error={false}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{
                        color: "#3ACADA",
                        "&:hover": {
                            color: "#2bb5c9",
                        },
                        "&:disabled": {
                            color: "rgba(0, 0, 0, 0.12)",
                        },
                    }}
                >
                    {t("close")}
                </Button>

                {selectedRoomIds.length > 0 && (
                    <Button
                        onClick={handleLinkRooms}
                        disabled={submitting || selectedRoomIds.length === 0}
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={16} /> : undefined}
                        sx={{
                            backgroundColor: "#3ACADA",
                            "&:hover": {
                                backgroundColor: "#2bb5c9",
                            },
                            "&:disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                        }}
                    >
                        {submitting ? t("linking") : t("linkSelectedRooms", { count: selectedRoomIds.length })}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default RoomLinkingModal;
